import React from "react";
import { useNavigate } from "react-router-dom";
import uiConfig from "../../config/uiConfig";

import { Box, Typography, Chip, IconButton } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import DataTable from "../../ui/Table";

const PRIORITY_ICON = {
  Low: <KeyboardDoubleArrowDownIcon sx={{ fontSize: 16, color: "#16a34a" }} />,
  Medium: <DragHandleIcon sx={{ fontSize: 16, color: "#ca8a04" }} />,
  High: <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16, color: "#ea580c" }} />,
  Critical: <PriorityHighIcon sx={{ fontSize: 16, color: "#dc2626" }} />,
};

const STATUS_ICON = {
  Open: <FiberNewOutlinedIcon sx={{ fontSize: 16, color: "#d97706" }} />,
  Assigned: <AssignmentIndOutlinedIcon sx={{ fontSize: 16, color: "#2563eb" }} />,
  "In Progress": <AutorenewIcon sx={{ fontSize: 16, color: "#7c3aed" }} />,
  Pending: <HourglassEmptyOutlinedIcon sx={{ fontSize: 16, color: "#c2410c" }} />,
  Resolved: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16a34a" }} />,
  Done: <DoneAllIcon sx={{ fontSize: 16, color: "#047857" }} />,
  Closed: <LockOutlinedIcon sx={{ fontSize: 16, color: "#475569" }} />,
};

const priorityStyle = (priority) => {
  switch (priority) {
    case "Critical":
      return { background: "#fee2e2", color: "#dc2626" };
    case "High":
      return { background: "#ffedd5", color: "#ea580c" };
    case "Medium":
      return { background: "#fef3c7", color: "#ca8a04" };
    case "Low":
      return { background: "#dcfce7", color: "#16a34a" };
    default:
      return {};
  }
};

const statusStyle = (status) => {
  switch (status) {
    case "Open":
      return { background: "#fef3c7", color: "#d97706" };
    case "Assigned":
      return { background: "#dbeafe", color: "#2563eb" };
    case "In Progress":
      return { background: "#ede9fe", color: "#7c3aed" };
    case "Pending":
      return { background: "#fff7ed", color: "#c2410c" };
    case "Resolved":
      return { background: "#dcfce7", color: "#16a34a" };
    case "Done":
      return { background: "#d1fae5", color: "#047857" };
    case "Closed":
      return { background: "#e5e7eb", color: "#475569" };
    default:
      return {};
  }
};

function TicketTable({
  tickets,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const navigate = useNavigate();

  const columns = [
    {
      key: "ticket_id",
      label: "Ticket",
      render: (ticket) => (
        <>
          <Box
            onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
            sx={{
              color: uiConfig.buttonColor,
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" }
            }}
          >
            {ticket.ticket_id}
          </Box>

          <Typography fontSize={12} color="#64748b" sx={{ mt: 0.6 }}>
            {ticket.created_by}
          </Typography>
        </>
      )
    },
    {
      key: "category",
      label: "Category",
      render: (ticket) => (
        <Chip label={ticket.category} size="small" sx={{ background: "#f1f5f9", fontWeight: 600 }} />
      )
    },
    {
      key: "priority",
      label: "Priority",
      render: (ticket) => (
        <Chip
          icon={PRIORITY_ICON[ticket.priority]}
          label={ticket.priority}
          size="small"
          sx={{ fontWeight: 600, ...priorityStyle(ticket.priority) }}
        />
      )
    },
    {
      key: "status",
      label: "Status",
      render: (ticket) => (
        <Chip
          icon={STATUS_ICON[ticket.status]}
          label={ticket.status}
          size="small"
          sx={{ fontWeight: 600, ...statusStyle(ticket.status) }}
        />
      )
    },
    {
      key: "assigned_name",
      label: "Assigned To",
      render: (ticket) => (
        <Typography fontSize={14}>{ticket.assigned_name || "Unassigned"}</Typography>
      )
    },
    {
      key: "project_name",
      label: "Project",
      render: (ticket) => (
        <Chip
          label={ticket.project_name || "No Project"}
          size="small"
          sx={{ background: "#ecfeff", color: "#155e75", fontWeight: 600 }}
        />
      )
    },
    {
      key: "actions",
      label: "Actions",
      width: 110,
      render: (ticket) => (
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={() => onEdit(ticket)} sx={{ background: "#eff6ff", mr: 1 }}>
            <EditIcon fontSize="small" color="primary" />
          </IconButton>

          <IconButton onClick={() => onDelete(ticket)} sx={{ background: "#fee2e2" }}>
            <DeleteIcon fontSize="small" sx={{ color: "#dc2626" }} />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      rows={tickets}
      loading={loading}
      rowKey={(ticket) => ticket.ticket_id}
      selectable
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      rowsPerPage={10}
      emptyState={{
        icon: SearchOffRoundedIcon,
        title: "No tickets match your view",
        subtitle: "Try changing filters or create a new ticket to get started."
      }}
    />
  );
}

export default TicketTable;
